<template>
  <div class="campaign-groups-container">
    <div class="groups-header">
      <div class="header-left">
        <el-button type="primary" @click="openCreateModal">
          <el-icon><Plus /></el-icon>
          Create Group
        </el-button>
        <el-input
          v-model="searchQuery"
          placeholder="Search"
          class="search-input"
          clearable
          @input="handleSearch"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
      </div>
      <div class="header-right">
        <el-button-group class="date-range-buttons">
          <el-button
            :type="activeDateRange === 'today' ? 'primary' : 'default'"
            @click="setDateRange('today')"
          >
            Today
          </el-button>
          <el-button
            :type="activeDateRange === 'yesterday' ? 'primary' : 'default'"
            @click="setDateRange('yesterday')"
          >
            Yesterday
          </el-button>
          <el-button
            :type="activeDateRange === 'last7days' ? 'primary' : 'default'"
            @click="setDateRange('last7days')"
          >
            Last 7 days
          </el-button>
          <el-button
            :type="activeDateRange === 'last30days' ? 'primary' : 'default'"
            @click="setDateRange('last30days')"
          >
            Last 30 days
          </el-button>
          <el-button
            :type="activeDateRange === 'thisMonth' ? 'primary' : 'default'"
            @click="setDateRange('thisMonth')"
          >
            This month
          </el-button>
          <el-button
            :type="activeDateRange === 'lastMonth' ? 'primary' : 'default'"
            @click="setDateRange('lastMonth')"
          >
            Last month
          </el-button>
        </el-button-group>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="3" animated />
    </div>

    <div v-else-if="items.length === 0" class="empty-state">
      <el-empty description="No campaign groups yet">
        <el-button type="primary" @click="openCreateModal">
          Create your first group
        </el-button>
      </el-empty>
    </div>

    <div v-else class="groups-grid">
      <CampaignGroupCard
        v-for="group in filteredItems"
        :key="group.id"
        :group="group"
        :stats="stats?.[group.id]"
        @edit="openEditModal"
        @delete="confirmDelete"
        @download-csv="downloadCsv"
      />
    </div>

    <CampaignGroupModal
      v-model="modalVisible"
      :edit-group="editingGroup"
      @saved="handleSaved"
    />

    <el-dialog v-model="deleteDialogVisible" title="Delete Group" width="400px">
      <p>
        Are you sure you want to delete group "{{
          deletingGroup?.attributes.name
        }}"?
      </p>
      <template #footer>
        <el-button @click="deleteDialogVisible = false">Cancel</el-button>
        <el-button type="danger" :loading="deleteLoading" @click="handleDelete">
          Delete
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue'
import { Plus, Search } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { useCampaignGroupsStore } from '@/apps/traforama/store/campaignGroups'
import { ICampaignGroup } from '@/apps/traforama/interfaces/campaignGroups'
import CampaignGroupCard from './CampaignGroupCard.vue'
import CampaignGroupModal from './CampaignGroupModal.vue'

type DateRangeType =
  | 'today'
  | 'yesterday'
  | 'last7days'
  | 'last30days'
  | 'thisMonth'
  | 'lastMonth'

export default defineComponent({
  name: 'CampaignGroupsList',
  components: {
    Plus,
    Search,
    CampaignGroupCard,
    CampaignGroupModal
  },
  setup() {
    const store = useCampaignGroupsStore()

    const searchQuery = ref('')
    const modalVisible = ref(false)
    const editingGroup = ref<ICampaignGroup | null>(null)
    const deleteDialogVisible = ref(false)
    const deletingGroup = ref<ICampaignGroup | null>(null)
    const deleteLoading = ref(false)
    const activeDateRange = ref<DateRangeType>('thisMonth')

    const loading = computed(() => store.loading)
    const items = computed(() => store.items)
    const stats = computed(() => store.stats)

    const filteredItems = computed(() => {
      if (!searchQuery.value) return items.value
      const query = searchQuery.value.toLowerCase()
      return items.value.filter((item) =>
        item.attributes.name.toLowerCase().includes(query)
      )
    })

    const formatDate = (date: Date): string => {
      return date.toISOString().split('T')[0]
    }

    const setDateRange = (range: DateRangeType) => {
      activeDateRange.value = range
      const today = new Date()
      let from: Date
      let to: Date = today

      switch (range) {
        case 'today':
          from = today
          break
        case 'yesterday':
          from = new Date(today)
          from.setDate(from.getDate() - 1)
          to = from
          break
        case 'last7days':
          from = new Date(today)
          from.setDate(from.getDate() - 6)
          break
        case 'last30days':
          from = new Date(today)
          from.setDate(from.getDate() - 29)
          break
        case 'thisMonth':
          from = new Date(today.getFullYear(), today.getMonth(), 1)
          break
        case 'lastMonth':
          from = new Date(today.getFullYear(), today.getMonth() - 1, 1)
          to = new Date(today.getFullYear(), today.getMonth(), 0)
          break
        default:
          from = new Date(today.getFullYear(), today.getMonth(), 1)
      }

      store.SetDateRange(formatDate(from), formatDate(to))
      store.FetchStats()
    }

    const handleSearch = () => {
      // Local filtering, no API call needed
    }

    const openCreateModal = () => {
      editingGroup.value = null
      modalVisible.value = true
    }

    const openEditModal = (group: ICampaignGroup) => {
      editingGroup.value = group
      modalVisible.value = true
    }

    const confirmDelete = (group: ICampaignGroup) => {
      deletingGroup.value = group
      deleteDialogVisible.value = true
    }

    const handleDelete = async () => {
      if (!deletingGroup.value) return

      deleteLoading.value = true
      try {
        await store.DestroyItem(parseInt(deletingGroup.value.id))
        ElMessage.success('Group deleted successfully')
        deleteDialogVisible.value = false
        deletingGroup.value = null
      } catch (e: any) {
        ElMessage.error(e.message || 'Failed to delete group')
      } finally {
        deleteLoading.value = false
      }
    }

    const downloadCsv = async (group: ICampaignGroup) => {
      try {
        await store.DownloadCsvReport(parseInt(group.id))
        ElMessage.success('CSV report downloaded')
      } catch (e: any) {
        ElMessage.error(e.message || 'Failed to download report')
      }
    }

    const handleSaved = () => {
      store.FetchStats()
    }

    onMounted(() => {
      store.FetchItems()
      setDateRange('thisMonth')
    })

    return {
      searchQuery,
      modalVisible,
      editingGroup,
      deleteDialogVisible,
      deletingGroup,
      deleteLoading,
      activeDateRange,
      loading,
      items,
      stats,
      filteredItems,
      setDateRange,
      handleSearch,
      openCreateModal,
      openEditModal,
      confirmDelete,
      handleDelete,
      downloadCsv,
      handleSaved
    }
  }
})
</script>

<style lang="scss" scoped>
.campaign-groups-container {
  padding: 20px;
}

.groups-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-input {
  width: 200px;
}

.header-right {
  display: flex;
  align-items: center;
}

.date-range-buttons {
  .el-button {
    font-size: 12px;
    padding: 8px 12px;
  }
}

.loading-container {
  padding: 40px;
}

.empty-state {
  padding: 60px 20px;
  text-align: center;
}

.groups-grid {
  display: flex;
  flex-wrap: wrap;
  margin: -10px;
}
</style>
